// src/pages/Admin/Fruits.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  MoreVert,
  Visibility,
  Inventory,
  Category,
  AttachMoney,
  Refresh,
  CloudUpload,
  Archive,
  Warning,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatCurrency, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminFruits = () => {
  const navigate = useNavigate();
  const [fruits, setFruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFruit, setSelectedFruit] = useState(null);
  const [bulkActions, setBulkActions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchFruits();
  }, []);

  const fetchFruits = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllFruits();
      setFruits(response.data);
    } catch (error) {
      console.error('Error fetching fruits:', error);
      toast.error('Failed to load fruits');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilter = (status) => {
    setFilter(status);
  };

  const handleMenuOpen = (event, fruit) => {
    setAnchorEl(event.currentTarget);
    setSelectedFruit(fruit);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFruit(null);
  };

  const handleEdit = () => {
    if (selectedFruit) {
      navigate(`/admin/fruits/edit/${selectedFruit._id}`);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialog(selectedFruit);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    try {
      await adminAPI.deleteFruit(deleteDialog._id);
      toast.success('Fruit deleted successfully');
      fetchFruits();
    } catch (error) {
      console.error('Error deleting fruit:', error);
      toast.error('Failed to delete fruit');
    } finally {
      setDeleteDialog(null);
    }
  };

  const handleToggleStatus = async (fruit) => {
    try {
      await adminAPI.updateFruit(fruit._id, { 
        ...fruit, 
        status: fruit.status === 'active' ? 'inactive' : 'active' 
      });
      toast.success(`Fruit ${fruit.status === 'active' ? 'deactivated' : 'activated'}`);
      fetchFruits();
    } catch (error) {
      console.error('Error updating fruit status:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredFruits = fruits.filter(fruit => {
    const matchesSearch = fruit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fruit.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fruit.variety.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || fruit.status === filter;
    return matchesSearch && matchesFilter;
  });

  const statusStats = {
    active: fruits.filter(f => f.status === 'active').length,
    inactive: fruits.filter(f => f.status === 'inactive').length,
    lowStock: fruits.filter(f => f.quantity < 100).length,
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Fruits Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your fruit inventory and listings
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/admin/fruits/add')}
          size="large"
        >
          Add New Fruit
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Fruits
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {fruits.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#2196f315', color: '#2196f3' }}>
                  <Inventory />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Active
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {statusStats.active}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Low Stock
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {statusStats.lowStock}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Categories
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {[...new Set(fruits.map(f => f.category))].length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#9c27b015', color: '#9c27b0' }}>
                  <Category />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search fruits by name, category, or variety..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label="All"
                onClick={() => handleFilter('all')}
                color={filter === 'all' ? 'primary' : 'default'}
                variant={filter === 'all' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Active"
                onClick={() => handleFilter('active')}
                color={filter === 'active' ? 'primary' : 'default'}
                variant={filter === 'active' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Inactive"
                onClick={() => handleFilter('inactive')}
                color={filter === 'inactive' ? 'primary' : 'default'}
                variant={filter === 'inactive' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Low Stock"
                onClick={() => handleFilter('lowStock')}
                color={filter === 'lowStock' ? 'primary' : 'default'}
                variant={filter === 'lowStock' ? 'filled' : 'outlined'}
              />
              <Button
                startIcon={<Refresh />}
                onClick={fetchFruits}
                variant="outlined"
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Fruits Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell width="60px">
                  <Typography variant="subtitle2" fontWeight="bold">#</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Fruit</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Category</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Variety</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Price</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Stock</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFruits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Inventory sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No fruits found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Try adjusting your search or filter
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/admin/fruits/add')}
                      >
                        Add Your First Fruit
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFruits
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((fruit, index) => (
                    <TableRow key={fruit._id} hover>
                      <TableCell>
                        <Avatar
                          src={fruit.images?.[0]}
                          sx={{ width: 40, height: 40 }}
                        >
                          {fruit.name.charAt(0)}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {fruit.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fruit.scientificName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={fruit.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {fruit.variety}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(fruit.pricePerKg)}/kg
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            MOQ: {fruit.minimumOrder} kg
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {fruit.quantity.toLocaleString()} kg
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color={fruit.quantity < 100 ? 'error' : 'text.secondary'}
                            >
                              {fruit.quantity < 100 ? 'Low Stock' : 'In Stock'}
                            </Typography>
                          </Box>
                          {fruit.quantity < 100 && (
                            <Badge color="error" variant="dot" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch
                            size="small"
                            checked={fruit.status === 'active'}
                            onChange={() => handleToggleStatus(fruit)}
                            color="success"
                          />
                          <Chip
                            label={fruit.status}
                            size="small"
                            color={getStatusColor(fruit.status)}
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/fruits/edit/${fruit._id}`)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog(fruit)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, fruit)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredFruits.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min(filteredFruits.length, rowsPerPage)} of {filteredFruits.length} fruits
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                size="small"
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(filteredFruits.length / rowsPerPage) - 1}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDialog)}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All data associated with this fruit will be permanently deleted.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteDialog?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => navigate(`/admin/fruits/${selectedFruit?._id}`)}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
        <MenuItem>
          <Archive sx={{ mr: 1 }} /> Archive
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default AdminFruits;